/**
 * Recovery Flow Component
 * Manages the display of recovery key modal when needed
 * Integrates device trust automation after successful recovery
 */

import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import RecoveryKeyModal from './RecoveryKeyModal';
import { useRecovery, useRecoveryActions, useShowRecoveryModal } from '../../context/RecoveryContext/index';
import { markRecoveryCompleted, RECOVERY_SKIPPED_KEY } from '../../context/RecoveryContext/storage';
import { useMatrixClient } from '../../context/MatrixContext';
import { useSettingsModal } from '../../context/SettingsModalContext';
import RecoveryKeyValidator from '../../services/RecoveryKeyValidator';
import { isRecoveryFlowEnabled, isDeviceTrustAutomationEnabled } from '../../config/featureFlags';
import deviceTrustManager from '../../crypto/DeviceTrustManager';
import VerificationModal from '../verification/VerificationModal';
import matrixAuthService from '../../services/matrixAuthService';
import logger from '../../utils/logger';

/**
 * RecoveryFlow component handles the recovery key modal display
 * and integrates with the recovery context and validator service
 */
const RecoveryFlow = () => {
  // Log on mount (using useRef to only log once)
  const didLogMount = useRef(false);
  if (!didLogMount.current) {
    logger.info('[RecoveryFlow] Component mounted');
    didLogMount.current = true;
  }

  const client = useMatrixClient();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();
  const shouldShowModal = useShowRecoveryModal();
  const { openSettings, isOpen: isSettingsOpen } = useSettingsModal();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [deviceTrustInProgress, setDeviceTrustInProgress] = useState(false);
  const backupSetupTriggeredRef = useRef(false);

  // Memoize isEnabled to avoid recalculating on every render
  const isEnabled = useMemo(() => isRecoveryFlowEnabled(), []);
  const isDeviceTrustEnabled = useMemo(() => isDeviceTrustAutomationEnabled(), []);

  // Debug logging - log on every render to trace issues
  useEffect(() => {
    logger.info('[RecoveryFlow] === State Update ===', {
      step: recoveryState.step,
      needsRecovery: recoveryState.needsRecovery,
      needsBackupSetup: recoveryState.needsBackupSetup,
      shouldShowModal,
      hasBackup: recoveryState.hasBackup,
      hasSSSS: recoveryState.hasSSSS,
      isEnabled,
      hasClient: !!client,
    });

    // Log what will happen
    if (!isEnabled) {
      logger.warn('[RecoveryFlow] Feature flag is DISABLED - modal will not show');
    } else if (!shouldShowModal && !recoveryState.needsBackupSetup) {
      logger.debug('[RecoveryFlow] Modal should NOT show (step is not AWAIT_KEY or ERROR, and no backup setup needed)');
    } else if (shouldShowModal) {
      logger.info('[RecoveryFlow] RecoveryKeyModal SHOULD be visible now');
    } else if (recoveryState.needsBackupSetup) {
      logger.info('[RecoveryFlow] Will open Settings to chat-backup for backup setup');
    }
  }, [recoveryState, shouldShowModal, isEnabled, client]);

  // Open Settings to Chat Backup page when needsBackupSetup is true
  useEffect(() => {
    if (recoveryState.needsBackupSetup && !backupSetupTriggeredRef.current) {
      logger.info('[RecoveryFlow] Opening settings to chat-backup - user has no SSSS');
      backupSetupTriggeredRef.current = true;
      openSettings('chat-backup');
    }
  }, [recoveryState.needsBackupSetup, openSettings]);

  useEffect(() => {
    if (shouldShowModal && isEnabled) {
      logger.debug('[RecoveryFlow] Recovery modal should be shown', {
        step: recoveryState.step,
        needsRecovery: recoveryState.needsRecovery,
      });
    }
  }, [shouldShowModal, isEnabled, recoveryState.step, recoveryState.needsRecovery]);

  /**
   * Handle device trust automation after recovery
   */
  const handleDeviceTrustAutomation = useCallback(async () => {
    if (!isDeviceTrustEnabled) {
      logger.debug('[RecoveryFlow] Device trust automation is disabled');
      return;
    }

    logger.info('[RecoveryFlow] Starting device trust automation');
    setDeviceTrustInProgress(true);

    try {
      // Run device trust automation
      const result = await deviceTrustManager.runAfterRecovery();

      logger.debug('[RecoveryFlow] Device trust result:', result);

      if (result.fallbackRequired) {
        logger.info('[RecoveryFlow] Fallback verification required');
        // Show verification modal for manual verification
        setShowVerificationModal(true);
      } else if (result.trusted) {
        logger.info('[RecoveryFlow] Device successfully trusted');
      }
    } catch (error) {
      logger.error('[RecoveryFlow] Device trust automation failed:', error);
      // Non-critical error, don't block recovery flow
    } finally {
      setDeviceTrustInProgress(false);
    }
  }, [isDeviceTrustEnabled]);

  /**
   * Handle successful recovery key submission or device verification
   */
  const handleSuccess = useCallback(async (result) => {
    logger.info('[RecoveryFlow] Recovery success', result);

    // Handle device verification completion (Transfer from Device flow)
    if (result?.verified) {
      logger.info('[RecoveryFlow] Device verification succeeded - checking for gossiped keys');

      try {
        const crypto = client?.getCrypto();

        if (crypto) {
          // 1. Check for gossiped backup key (SDK auto-gossips after verification)
          // The Matrix SDK automatically shares m.megolm_backup.v1 between verified devices
          try {
            await crypto.checkSecrets('m.megolm_backup.v1');
          } catch (secretError) {
            logger.debug('[RecoveryFlow] checkSecrets not available or failed:', secretError.message);
          }

          // 2. Try to get the backup key (if received via gossip)
          const backupKey = await crypto.getSessionBackupPrivateKey();

          if (backupKey) {
            logger.info('[RecoveryFlow] Backup key received via gossip - restoring');

            // 3. Restore key backup using the gossiped key
            try {
              await crypto.restoreKeyBackup();
              logger.info('[RecoveryFlow] Key backup restored successfully');
            } catch (restoreError) {
              logger.warn('[RecoveryFlow] Key backup restore failed:', restoreError.message);
            }

            // NOTE: Do NOT call handleDeviceTrustAutomation() here!
            // The SAS verification that just completed IS the device trust verification.
            // Calling it again would check device trust status which may not have propagated yet,
            // causing fallbackRequired=true and opening another verification modal (infinite loop).
          } else {
            logger.debug('[RecoveryFlow] No backup key received via gossip');
          }
        }

        // 5. Re-detect recovery requirement based on current crypto state
        const recoveryCheck = await matrixAuthService.detectRecoveryRequirement(client);

        if (!recoveryCheck?.needsRecovery) {
          logger.info('[RecoveryFlow] Recovery no longer needed - completing');
          // Mark recovery as complete
          if (recoveryActions?.complete) {
            recoveryActions.complete();
          }

          // Mark in localStorage for this device
          const deviceId = client?.getDeviceId();
          if (deviceId) {
            markRecoveryCompleted(deviceId);
          }
        } else {
          logger.warn('[RecoveryFlow] Still needs recovery after verification - keeping modal open for manual key entry');
          // User still needs to enter recovery key manually
          // Don't mark as complete - let modal stay open
        }
      } catch (error) {
        logger.error('[RecoveryFlow] Error processing post-verification recovery:', error);
        // Fall back to keeping modal open for manual recovery key entry
        // Mark as verified but don't complete recovery
        logger.info('[RecoveryFlow] Falling back - marking verification complete but keeping recovery modal');
        if (recoveryActions?.complete) {
          recoveryActions.complete();
        }
        const deviceId = client?.getDeviceId();
        if (deviceId) {
          markRecoveryCompleted(deviceId);
        }
      }
    } else {
      // Handle recovery key entry (traditional flow)
      logger.info('[RecoveryFlow] Recovery key validated successfully');

      // The validator has already stored the key
      // Trigger the recovery flow actions if available
      if (recoveryActions.restore) {
        try {
          await recoveryActions.restore();

          // After successful restore, run device trust automation
          await handleDeviceTrustAutomation();
        } catch (error) {
          logger.error('[RecoveryFlow] Failed to restore after validation:', error);
        }
      }
    }
  }, [recoveryActions, handleDeviceTrustAutomation, client]);

  /**
   * Handle skip recovery
   */
  const handleSkip = useCallback(() => {
    logger.info('[RecoveryFlow] User skipped recovery');

    if (recoveryActions.skip) {
      recoveryActions.skip();
    }
  }, [recoveryActions]);

  /**
   * Handle reset backup
   * After reset, navigate to Chat Backup page for new key generation
   */
  const handleReset = useCallback(async () => {
    logger.info('[RecoveryFlow] User requested backup reset');

    if (recoveryActions.reset) {
      try {
        await recoveryActions.reset();
        // Mark recovery as skipped for this session to prevent modal from reappearing
        // during re-initialization (server still has SSSS until new key is activated)
        sessionStorage.setItem(RECOVERY_SKIPPED_KEY, 'true');
        logger.debug('[RecoveryFlow] Marked recovery as skipped to prevent modal reappearance');
        // Set flag for ChatBackupPage to force key generation
        sessionStorage.setItem('forceKeyGeneration', 'true');
        // Navigate to Chat Backup page to show key generation flow
        logger.info('[RecoveryFlow] Reset complete, opening settings to chat-backup');
        openSettings('chat-backup');
      } catch (error) {
        logger.error('[RecoveryFlow] Failed to reset backup:', error);
      }
    }
  }, [recoveryActions, openSettings]);

  /**
   * Handle verification modal completion
   */
  const handleVerificationComplete = useCallback((success) => {
    logger.info('[RecoveryFlow] Verification modal completed:', success);
    setShowVerificationModal(false);
  }, []);

  // Create validator configuration
  const validator = {
    onSubmit: async (recoveryKey) => {
      try {
        // Use RecoveryKeyValidator to validate and store the key
        const result = await RecoveryKeyValidator.validateAndSubmit(
          recoveryKey,
          client,
          recoveryActions.dispatch,
        );

        return result;
      } catch (error) {
        logger.error('[RecoveryFlow] Validation error:', error);
        return {
          success: false,
          error: error.code || 'UNKNOWN',
        };
      }
    },
  };

  // Don't render if feature is disabled
  if (!isEnabled) {
    logger.debug('[RecoveryFlow] Not rendering - feature is disabled');
    return null;
  }

  // Don't render if modal shouldn't be shown
  // Note: needsBackupSetup case is handled by useEffect that opens Settings
  if (!shouldShowModal || isSettingsOpen) {
    logger.debug('[RecoveryFlow] Not rendering - shouldShowModal is false or settings is open');
    return null;
  }

  logger.debug('[RecoveryFlow] Rendering RecoveryKeyModal');

  return (
    <>
      {shouldShowModal && (
        <RecoveryKeyModal
          validator={validator}
          onSuccess={handleSuccess}
          onSkip={handleSkip}
          onReset={handleReset}
          allowSkip={true}
          allowReset={true}
        />
      )}

      {showVerificationModal && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onComplete={handleVerificationComplete}
        />
      )}

      {deviceTrustInProgress && (
        <div className="device-trust-indicator">
          Verifying device...
        </div>
      )}
    </>
  );
};

export default RecoveryFlow;